import { motion } from "framer-motion-3d";
import { MotionConfig } from "framer-motion";
import { useRef, useLayoutEffect } from "react";
import { transition } from "./settings";
import { Canvas, useThree } from "@react-three/fiber";
import { useSmoothTransform } from "./useSmoothTransform";
import { useLoader } from '@react-three/fiber';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

import tracts1 from './images/tracts1.glb';
import tracts2 from './images/tracts2.glb';
import tracts3 from './images/tracts3.glb';
import tracts4 from './images/tracts4.glb';

export function Objects({ isHover, isPress, mouseX, mouseY }) {
  const lightRotateX = useSmoothTransform(mouseY, spring, mouseToLightRotation);
  const lightRotateY = useSmoothTransform(mouseX, spring, mouseToLightRotation);

  const gltf1 = useLoader(GLTFLoader, tracts1)
  const gltf2 = useLoader(GLTFLoader, tracts2)
  const gltf3 = useLoader(GLTFLoader, tracts3)
  const gltf4 = useLoader(GLTFLoader, tracts4)

  return (
    <Canvas shadows dpr={[1, 2]} resize={{ scroll: false, offsetSize: true }}>
      <Camera mouseX={mouseX} mouseY={mouseY} />
      <MotionConfig transition={transition}>
        <motion.group
          center={[0, 0, 0]}
          rotation={[lightRotateX, lightRotateY, 0]}
        >
          <Lights />
        </motion.group>
        <motion.group
          initial={false}
          animate={isHover ? "hover" : "rest"}
          dispose={null}
          variants={{
            hover: { z: isPress ? -0.9 : 0 }
          }}
        >
          <Tracts1 obj={gltf1}/>
          <Tracts2 obj={gltf2}/>
          <Tracts3 obj={gltf3}/>
          <Tracts4 obj={gltf4}/>
        </motion.group>
      </MotionConfig>
    </Canvas>
  );
}

export function Lights() {
  return (
    <>
      <spotLight color="#0dffef" position={[-10, -10, -10]} intensity={0.2} />
      <spotLight color="#18fc03" position={[-10, 0, 15]} intensity={0.8} />
      <spotLight color="#3b44ff" position={[-5, 20, 2]} intensity={0.5} />
      <spotLight color="#ff33ff" position={[15, 10, -2]} intensity={2} />
      <spotLight color="#0a29c2" position={[15, 10, 5]} intensity={1} />
      <spotLight color="#4ac20a" position={[5, -10, 5]} intensity={0.8} />
    </>
  );
}

export function Tracts1(obj) {
  return (
    <motion.mesh position={[-0.5, -0.5, 0]} variants={{ hover: { z: 2 } }}>
      <primitive object={obj.obj.scene} />
    </motion.mesh>
  );
}

export function Tracts2(obj) {
  return (
    <motion.mesh
      position={[-0.8, 0.4, 0]}
      rotation={[-0.5, 0, -0.3]}
      variants={{
        hover: {
          z: 1.1,
          x: -1.5,
          rotateX: -0.2,
          rotateZ: 0.4
        }
      }}
    >
      <primitive object={obj.obj.scene} />
    </motion.mesh>
  );
}

export function Tracts3(obj) {
  return (
    <motion.mesh
      position={[0.1, 0.4, 0]}
      rotation={[-0.5, 0.5, 0]}
      variants={{
        hover: {
          y: 0.5,
          z: 2,
          rotateY: -0.2
        }
      }}
    >
      <primitive object={obj.obj.scene} />
    </motion.mesh>
  );
}

export function Tracts4(obj) {
  return (
    <motion.mesh
      position={[1.1, 0, 0]}
      rotation-z={0.5}
      variants={{
        hover: {
          x: 1.8,
          z: 0.6,
          y: 0.6,
          rotateZ: -0.5
        }
      }}
    >
      <primitive object={obj.obj.scene} />
    </motion.mesh>
  );
}

// Adapted from https://github.com/pmndrs/drei/blob/master/src/core/PerspectiveCamera.tsx
function Camera({ mouseX, mouseY, ...props }) {
  const cameraX = useSmoothTransform(mouseX, spring, (x) => x / 350);
  const cameraY = useSmoothTransform(mouseY, spring, (y) => (-1 * y) / 350);

  const set = useThree(({ set }) => set);
  const camera = useThree(({ camera }) => camera);
  const size = useThree(({ size }) => size);
  const scene = useThree(({ scene }) => scene);
  const cameraRef = useRef();

  useLayoutEffect(() => {
    const { current: cam } = cameraRef;
    if (cam) {
      cam.aspect = size.width / size.height;
      cam.updateProjectionMatrix();
    }
  }, [size, props]);

  useLayoutEffect(() => {
    if (cameraRef.current) {
      const oldCam = camera;
      set(() => ({ camera: cameraRef.current }));
      return () => set(() => ({ camera: oldCam }));
    }
  }, [camera, cameraRef, set]);

  useLayoutEffect(() => {
    return cameraX.onChange(() => camera.lookAt(scene.position));
  }, [cameraX]);

  return (
    <motion.perspectiveCamera
      ref={cameraRef}
      fov={90}
      position={[cameraX, cameraY, 3.8]}
    />
  );
}

const spring = { stiffness: 600, damping: 30 };

const mouseToLightRotation = (v) => (-1 * v) / 140;
